/**
 * As planilhas do "Exportar" dos relatórios (08/10/2026).
 *
 * ── O que vai no arquivo ──────────────────────────────────────────────────
 *
 * O que a tela mostra, na ordem em que mostra, e nada além: no topo, de onde
 * são os números (relatório, período, filtros, de quem); depois um bloco por
 * cartão. Os números saem como NÚMERO, e não como texto formatado: "R$ 12 mil"
 * não soma no Excel, e quem exporta quer somar.
 *
 * ── O que NÃO vai ─────────────────────────────────────────────────────────
 *
 * As listas que abrem num diálogo (as vendas de uma pessoa, as oportunidades
 * paradas de uma etapa). Elas trazem o título da oportunidade, que costuma ter
 * o nome do cliente, e um arquivo com dado de cliente pede o caminho da
 * exportação de clientes: rota própria, permissão e registro de acesso. Aqui
 * só há agregado e o nome de quem trabalha na organização, que a tela já
 * mostra a quem tem a permissão de indicador.
 *
 * ── Por que aqui, e não no servidor ───────────────────────────────────────
 *
 * Pela mesma razão da exportação de clientes (`clientes/planilha.ts`): o
 * controle é da LEITURA, e ela já aconteceu pela rota do relatório. O formato
 * é apresentação.
 */

import type {
  AtendimentoDoPeriodo,
  Comparativos,
  Dashboard,
  GargalosDoFunil,
  NumeroComparado,
  RankingDeVendas,
} from '@cliqcrm/contracts';
import {
  planilhaEmBlocos,
  valorNaPlanilha,
  type BlocoDaPlanilha,
  type Celula,
  type Tabela,
} from '@/shared/lib/planilha';
import { nomeDoCanal } from '@/entities/atendimento/canal';
import { ABRANGENCIA } from './formato';

type Topo = readonly (readonly [string, string])[];

/** `2026-10-08` → `08/10/2026`, como o Excel pt-BR lê data. */
function data(dia: string): string {
  const [ano, mes, d] = dia.split('-');
  return ano && mes && d ? `${d}/${mes}/${ano}` : dia;
}

const periodo = (p: { de: string; ate: string }): string => `${data(p.de)} a ${data(p.ate)}`;

const deQuem = (abrangencia: string): string => ABRANGENCIA[abrangencia] ?? abrangencia;

/** Um decimal, como número de planilha. Nulo vira célula vazia. */
function decimal(valor: number | null): number | null {
  return valor === null || !Number.isFinite(valor) ? null : Math.round(valor * 10) / 10;
}

/** Segundos → minutos, que é a unidade que se compara numa planilha. */
const minutos = (segundos: number | null): number | null =>
  decimal(segundos === null ? null : segundos / 60);

const nomeOuSemNome = (nome: string | null): string => nome ?? 'Sem nome';

/** `relatorio-ranking-2026-10-01-a-2026-10-07.xlsx`: o período no nome, porque o arquivo envelhece. */
export function nomeDaPlanilha(secao: string, p: { de: string; ate: string }): string {
  return `relatorio-${secao}-${p.de}-a-${p.ate}.xlsx`;
}

// ── Ranking de vendas ──────────────────────────────────────────────────────

export function planilhaDoRanking(
  dados: RankingDeVendas,
  filtros: { readonly funil?: string | undefined; readonly time?: string | undefined } = {},
): Tabela {
  const topo: Topo = [
    ['Relatório', 'Ranking de vendas'],
    ['Período', periodo(dados.periodo)],
    ['Período anterior', periodo(dados.periodo_anterior)],
    ['Funil', filtros.funil ?? 'Todos'],
    ['Equipe', filtros.time ?? 'Todas'],
    ['Números de', deQuem(dados.abrangencia)],
  ];
  const moeda = dados.moeda;
  const blocos: BlocoDaPlanilha[] = [
    {
      titulo: 'Ranking',
      colunas: [
        'Posição',
        'Pessoa',
        'Vendas',
        `Valor vendido (${moeda})`,
        `Ticket médio (${moeda})`,
        'Posição no período anterior',
      ],
      linhas: dados.itens.map((item) => [
        item.posicao,
        nomeOuSemNome(item.nome),
        item.quantidade,
        valorNaPlanilha(item.valor_micros),
        valorNaPlanilha(item.ticket_medio_micros),
        item.posicao_anterior ?? '',
      ]),
    },
  ];
  if (dados.sem_vendas.length > 0) {
    blocos.push({
      titulo: 'Sem vendas no período',
      colunas: ['Pessoa'],
      linhas: dados.sem_vendas.map((pessoa) => [nomeOuSemNome(pessoa.nome)]),
    });
  }
  blocos.push({
    titulo: 'Fora do ranking',
    colunas: ['O quê', 'Quantidade', `Valor (${moeda})`],
    linhas: [
      [
        'Vendas sem responsável',
        dados.sem_responsavel.quantidade,
        valorNaPlanilha(dados.sem_responsavel.valor_micros),
      ],
      ['Vendas sem oportunidade', dados.vendas_sem_oportunidade, ''],
      ['Vendas sem valor', dados.vendas_sem_valor, ''],
    ],
  });
  return planilhaEmBlocos(topo, blocos);
}

// ── Comparativos ───────────────────────────────────────────────────────────

const BASE: Record<string, string> = {
  anterior: 'Período anterior',
  mes_anterior: 'Mês anterior',
  ano_anterior: 'Ano anterior',
};

const UNIDADE: Record<NumeroComparado['formato'], (moeda: string) => string> = {
  numero: () => 'quantidade',
  dinheiro: (moeda) => moeda,
  duracao: () => 'minutos',
};

function valorComparado(item: NumeroComparado, valor: number | null): Celula {
  if (valor === null) return null;
  if (item.formato === 'dinheiro') return valorNaPlanilha(valor);
  if (item.formato === 'duracao') return minutos(valor);
  return valor;
}

export function planilhaDosComparativos(dados: Comparativos): Tabela {
  const topo: Topo = [
    ['Relatório', 'Comparativos'],
    ['Período', periodo(dados.periodo)],
    ['Comparado com', `${BASE[dados.base.tipo] ?? dados.base.tipo}: ${periodo(dados.base)}`],
  ];
  return planilhaEmBlocos(topo, [
    {
      titulo: 'Indicadores',
      colunas: [
        'Grupo',
        'Indicador',
        'Unidade',
        'No período',
        'Na base',
        'Variação (%)',
        'Melhor quando',
        'Números de',
      ],
      linhas: dados.itens.map((item) => [
        item.grupo === 'atendimento' ? 'Atendimento' : 'Comercial',
        item.nome,
        UNIDADE[item.formato](dados.moeda),
        valorComparado(item, item.atual),
        valorComparado(item, item.base),
        decimal(item.variacao_percentual),
        item.melhor_quando === 'maior' ? 'maior' : 'menor',
        deQuem(item.abrangencia),
      ]),
    },
  ]);
}

// ── Gargalos do funil ─────────────────────────────────────────────────────

export function planilhaDosGargalos(dados: GargalosDoFunil): Tabela {
  const topo: Topo = [
    ['Relatório', 'Gargalos do funil'],
    ['Período', periodo(dados.periodo)],
    ['Funil', dados.funil?.nome ?? 'Nenhum'],
    ['Parada é', `sem mudar de etapa há mais de ${dados.parada_dias} dias`],
    ['Números de', deQuem(dados.abrangencia)],
  ];
  const blocos: BlocoDaPlanilha[] = [
    {
      titulo: 'Etapas',
      colunas: [
        'Etapa',
        'Em aberto',
        'Paradas',
        'Mais antiga (dias)',
        'Entradas no período',
        'Avançaram',
        'Tempo médio na etapa (horas)',
      ],
      linhas: dados.etapas.map((etapa) => [
        etapa.nome,
        etapa.em_aberto,
        etapa.paradas,
        etapa.mais_antiga_dias ?? '',
        etapa.entradas,
        etapa.avancaram,
        decimal(etapa.tempo_medio_horas),
      ]),
    },
  ];
  if (dados.responsaveis.length > 0) {
    blocos.push({
      titulo: 'Paradas por responsável',
      colunas: ['Responsável', 'Paradas'],
      linhas: dados.responsaveis.map((r) => [nomeOuSemNome(r.nome), r.paradas]),
    });
  }
  return planilhaEmBlocos(topo, blocos);
}

// ── Atendimento ───────────────────────────────────────────────────────────

const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

export function planilhaDoAtendimento(dados: AtendimentoDoPeriodo): Tabela {
  const topo: Topo = [
    ['Relatório', 'Atendimento'],
    ['Período', periodo(dados.periodo)],
    ['Volume de', deQuem(dados.abrangencia_do_volume)],
  ];
  const { resumo } = dados;
  const blocos: BlocoDaPlanilha[] = [
    {
      titulo: 'Resumo',
      colunas: ['Indicador', 'Valor'],
      linhas: [
        ['Atendimentos', resumo.atendimentos],
        ['Resolvidas', resumo.resolvidas],
        ['Sem resposta', resumo.sem_resposta],
        [
          'Espera mediana pela primeira resposta (minutos)',
          minutos(resumo.espera_mediana_segundos),
        ],
        ['Espera de 90% dos atendimentos (minutos)', minutos(resumo.espera_p90_segundos)],
      ],
    },
    {
      titulo: 'Por canal',
      colunas: ['Canal', 'Atendimentos', 'Mensagens recebidas', 'Espera mediana (minutos)'],
      linhas: dados.por_canal.map((c) => [
        nomeDoCanal(c.canal) ?? c.canal,
        c.atendimentos,
        c.mensagens_recebidas,
        minutos(c.espera_mediana_segundos),
      ]),
    },
    {
      titulo: 'Mensagens recebidas por dia e hora',
      colunas: ['Dia da semana', 'Hora', 'Mensagens recebidas'],
      linhas: dados.por_hora.map((c) => [
        DIAS[c.dia_semana] ?? '',
        `${c.hora}h`,
        c.mensagens_recebidas,
      ]),
    },
    {
      titulo: `Por pessoa (${deQuem(dados.por_pessoa.abrangencia)})`,
      colunas: ['Pessoa', 'Conversas', 'Mensagens enviadas'],
      linhas: dados.por_pessoa.itens.map((p) => [
        nomeOuSemNome(p.nome),
        p.conversas,
        p.mensagens_enviadas,
      ]),
    },
  ];
  return planilhaEmBlocos(topo, blocos);
}

// ── Visão geral ───────────────────────────────────────────────────────────

/**
 * Os cartões do painel. A saúde das integrações fica de fora: é estado de
 * operação de agora, e não número do período.
 */
export function planilhaDaVisaoGeral(dados: Dashboard): Tabela {
  const topo: Topo = [
    ['Relatório', 'Visão geral'],
    ['Período', periodo(dados.periodo)],
  ];
  const { atendimento, comercial, funil, resultado } = dados;
  return planilhaEmBlocos(topo, [
    {
      titulo: `Atendimento por dia (${deQuem(atendimento.abrangencia)})`,
      colunas: [
        'Dia',
        'Conversas iniciadas',
        'Conversas resolvidas',
        'Mensagens recebidas',
        'Mensagens enviadas',
        'Conversas sem resposta',
      ],
      linhas: atendimento.por_dia.map((d) => [
        data(d.dia),
        d.conversas_iniciadas,
        d.conversas_resolvidas,
        d.mensagens_recebidas,
        d.mensagens_enviadas,
        d.conversas_sem_resposta,
      ]),
    },
    {
      titulo: `Atendimento por canal (${deQuem(atendimento.abrangencia)})`,
      colunas: ['Canal', 'Conversas iniciadas', 'Mensagens recebidas', 'Mensagens enviadas'],
      linhas: atendimento.por_canal.map((c) => [
        nomeDoCanal(c.canal) ?? c.canal,
        c.conversas_iniciadas,
        c.mensagens_recebidas,
        c.mensagens_enviadas,
      ]),
    },
    {
      titulo: `Comercial por dia (${deQuem(comercial.abrangencia)})`,
      colunas: ['Dia', 'Oportunidades criadas', 'Oportunidades fechadas', 'Valor fechado'],
      linhas: comercial.por_dia.map((d) => [
        data(d.dia),
        d.oportunidades_criadas,
        d.oportunidades_fechadas,
        valorNaPlanilha(d.valor_fechado_micros),
      ]),
    },
    {
      titulo: `Funil por etapa (${deQuem(funil.abrangencia)})`,
      colunas: ['Funil', 'Etapa', 'Etapa final', 'Entradas', 'Em aberto', 'Valor em aberto'],
      linhas: funil.por_etapa.map((e) => [
        e.funil_nome,
        e.nome,
        e.final ? 'sim' : 'não',
        e.entradas,
        e.em_aberto ?? '',
        valorNaPlanilha(e.valor_em_aberto_micros),
      ]),
    },
    {
      titulo: `Resultado por funil (${deQuem(resultado.abrangencia)})`,
      colunas: ['Funil', 'Ganhas', 'Perdidas', 'Valor ganho'],
      linhas: resultado.por_funil.map((f) => [
        f.nome,
        f.ganhas,
        f.perdidas,
        valorNaPlanilha(f.valor_ganho_micros),
      ]),
    },
    {
      titulo: `Motivos de perda (${deQuem(resultado.abrangencia)})`,
      colunas: ['Motivo', 'Quantidade'],
      linhas: resultado.motivos_de_perda.map((m) => [m.motivo ?? 'Sem motivo', m.quantidade]),
    },
  ]);
}
